import { useCallback, useEffect, useState } from "react";
import { RefreshCw, CheckCircle2, XCircle, Pencil, UtensilsCrossed, BedDouble, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner } from "../components/common";

/* Admin: background batch regeneration of the oldest cached AI articles. */
function BulkRefreshCard() {
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(20);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/ai-content/bulk-refresh/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!st?.status?.running) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [st?.status?.running, load]);
  const start = async () => {
    setBusy(true);
    try {
      await api.post("/admin/ai-content/bulk-refresh", { older_than_days: Number(days) || 30, limit: Number(limit) || 20 });
      toast.success("Bulk refresh queued — runs in the background");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start bulk refresh"); }
    finally { setBusy(false); }
  };
  const s = st?.status;
  return (
    <div className="mb-8 border border-zinc-300 bg-white p-4" data-testid="admin-bulk-refresh">
      <h2 className="font-heading font-900 text-xl flex items-center gap-2 mb-1"><RefreshCw className="w-5 h-5" /> Bulk article refresh</h2>
      <p className="text-xs text-muted-foreground mb-3">
        Regenerate the oldest cached airline, airport and aircraft articles in a background batch (oldest first).
        {st?.article_counts && ` Cached now: ${st.article_counts.airline || 0} airlines · ${st.article_counts.airport || 0} airports · ${st.article_counts.aircraft || 0} aircraft.`}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-700 text-zinc-600">Older than
          <input type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} data-testid="admin-bulk-days"
            className="border border-zinc-300 px-2 py-1.5 text-sm w-20 ml-1.5" /> days
        </label>
        <label className="text-xs font-700 text-zinc-600">Max
          <input type="number" min="1" max="100" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="admin-bulk-limit"
            className="border border-zinc-300 px-2 py-1.5 text-sm w-20 ml-1.5" /> articles
        </label>
        <button onClick={start} disabled={busy || s?.running} data-testid="admin-bulk-start"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${s?.running ? "animate-spin" : ""}`} /> {s?.running ? "Running…" : "Start bulk refresh"}
        </button>
      </div>
      {s && (
        <p className={`text-xs mt-2 ${s.running ? "text-indigo-700" : "text-emerald-700"}`} data-testid="admin-bulk-status">
          {s.running
            ? `⏳ ${s.done + s.failed}/${s.total} processed${s.current ? ` · working on ${s.current}` : ""}${s.failed ? ` · ${s.failed} failed` : ""}`
            : s.finished_at ? `✓ Last batch: ${s.done} regenerated${s.failed ? `, ${s.failed} failed` : ""} of ${s.total} · finished ${s.finished_at.slice(0, 16).replace("T", " ")}` : null}
          {s.last_error && <span className="text-rose-600"> · last error: {s.last_error}</span>}
        </p>
      )}
    </div>
  );
}

/* Admin: regenerate a stale AI profile article for any airline, airport or aircraft. */
function ArticleRefreshCard() {
  const [kind, setKind] = useState("airline");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const refresh = async () => {
    if (!code.trim()) { toast.error("Enter an IATA/ICAO code or id"); return; }
    setBusy(true); setLast(null);
    try {
      const r = await api.post("/admin/ai-content/refresh", { type: kind, code: code.trim() }, { timeout: 120000 });
      setLast(r.data);
      toast.success(`${r.data.name} article regenerated`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not regenerate the article"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-8 border border-zinc-300 bg-white p-4" data-testid="admin-article-refresh">
      <h2 className="font-heading font-900 text-xl flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5" /> AI article refresh</h2>
      <p className="text-xs text-muted-foreground mb-3">Regenerate a stale airline, airport or aircraft profile article. The fresh version replaces the cached one instantly.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="admin-refresh-type" className="border border-zinc-300 px-2 py-1.5 text-sm">
          <option value="airline">Airline</option>
          <option value="airport">Airport</option>
          <option value="aircraft">Aircraft</option>
        </select>
        <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && refresh()}
          placeholder="IATA / ICAO code or id (e.g. EK, HYD, 77W)" data-testid="admin-refresh-code"
          className="border border-zinc-300 px-2 py-1.5 text-sm flex-1 min-w-[220px]" />
        <button onClick={refresh} disabled={busy} data-testid="admin-refresh-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-700 text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Regenerating…" : "Regenerate article"}
        </button>
      </div>
      {last && (
        <p className="text-xs text-emerald-700 mt-2" data-testid="admin-refresh-result">
          ✓ {last.name} — “{last.tagline}” · regenerated {last.generated_at?.slice(0, 16).replace("T", " ")}
        </p>
      )}
    </div>
  );
}

/* Admin: Food & Stay guide overview + business submission queue. */
export default function AdminIntelligence() {
  const [ov, setOv] = useState(null);
  const [subs, setSubs] = useState(null);
  const [subFilter, setSubFilter] = useState("PENDING");
  const [editing, setEditing] = useState(null); // submission being edited
  const [ef, setEf] = useState({});

  const load = useCallback(() => {
    api.get("/business/intel-overview").then((r) => setOv(r.data)).catch(() => setOv({ food: [], stay: [] }));
  }, []);
  const loadSubs = useCallback(() => {
    api.get("/business/submissions", { params: { status: subFilter } }).then((r) => setSubs(r.data.items)).catch(() => setSubs([]));
  }, [subFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSubs(); }, [loadSubs]);

  const regen = async (kind, slug) => {
    try { await api.post(`/${kind === "food" ? "food" : "stays"}/${slug}/regenerate`); toast.success(`${slug} queued for regeneration`); load(); }
    catch { toast.error("Could not regenerate"); }
  };
  const publish = async (sid) => {
    try { await api.post(`/business/submissions/${sid}/publish`); toast.success("Published — business notified"); loadSubs(); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
  };
  const reject = async (sid) => {
    const reason = window.prompt("Reason (sent to the business):");
    if (reason === null) return;
    try { await api.post(`/business/submissions/${sid}/reject`, { reason }); toast.success("Rejected — business notified"); loadSubs(); load(); }
    catch { toast.error("Could not reject"); }
  };
  const saveEdit = async () => {
    try { await api.patch(`/business/submissions/${editing}`, ef); toast.success("Submission updated"); setEditing(null); loadSubs(); }
    catch { toast.error("Could not save"); }
  };

  if (!ov) return <Spinner full />;
  const inp = "border border-zinc-300 px-2 py-1.5 text-sm w-full";

  const GuideTable = ({ kind, rows, Icon }) => (
    <div className="mb-8" data-testid={`admin-${kind}-guides`}>
      <h2 className="font-heading font-900 text-xl flex items-center gap-2 mb-2"><Icon className="w-5 h-5" /> {kind === "food" ? "Food & Dining" : "Hotels & Accommodation"} guides ({rows.length})</h2>
      <div className="border border-zinc-300 bg-white divide-y max-h-80 overflow-y-auto">
        {rows.length === 0 && <div className="p-3 text-sm text-muted-foreground">None generated yet.</div>}
        {rows.map((g) => (
          <div key={g.slug} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`admin-guide-${kind}-${g.slug}`}>
            <span className="font-700 min-w-[120px]">{g.name || g.slug}</span>
            <span className={`text-[11px] font-700 px-1.5 py-0.5 rounded ${g.status === "PUBLISHED" ? "bg-emerald-100 text-emerald-800" : g.status === "FAILED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{g.status}</span>
            <span className="text-xs text-muted-foreground">{g.place_count || 0} places · v{g.version || 0} · {g.generated_at?.slice(0, 10) || "—"}</span>
            {g.last_error && <span className="text-xs text-red-600 truncate max-w-[200px]" title={g.last_error}>{g.last_error}</span>}
            <span className="ml-auto flex gap-2">
              <a href={`/${kind === "food" ? "food-dining" : "hotels-accommodation"}/${g.slug}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">view</a>
              <button onClick={() => regen(kind, g.slug)} data-testid={`admin-regen-${kind}-${g.slug}`} className="text-xs font-700 text-indigo-700 hover:underline inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> regenerate</button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-intelligence-page">
      <h1 className="font-heading font-900 text-3xl mb-1">Destination Intelligence</h1>
      <p className="text-sm text-muted-foreground mb-6">Food & Stay guide status, regeneration, and business submission review. {ov.pending_submissions > 0 && <b className="text-amber-700">{ov.pending_submissions} submission(s) pending.</b>}</p>

      <GuideTable kind="food" rows={ov.food || []} Icon={UtensilsCrossed} />
      <GuideTable kind="stay" rows={ov.stay || []} Icon={BedDouble} />

      <ArticleRefreshCard />
      <BulkRefreshCard />

      <div className="flex items-center gap-2 mb-2">
        <h2 className="font-heading font-900 text-xl">Business submissions</h2>
        {["PENDING", "PUBLISHED", "REJECTED", "all"].map((s) => (
          <button key={s} onClick={() => setSubFilter(s)} data-testid={`admin-subs-filter-${s}`}
            className={`px-2 py-1 text-xs font-700 border ${subFilter === s ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300"}`}>{s}</button>
        ))}
      </div>
      <div className="space-y-3" data-testid="admin-submissions">
        {!subs ? <Spinner /> : subs.length === 0 ? <p className="text-sm text-muted-foreground border border-dashed p-4">No {subFilter.toLowerCase()} submissions.</p> : subs.map((s) => (
          <div key={s.id} className="border border-zinc-300 bg-white p-4" data-testid={`admin-sub-${s.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-700">{s.name}</span>
              <span className="text-[11px] font-700 px-1.5 py-0.5 rounded bg-zinc-100">{s.kind} · {s.business_type || "?"}</span>
              <span className="text-xs text-muted-foreground">{s.destination} · by {s.submitter_name || "user"} ({s.submitter_role})</span>
              <span className={`text-[11px] font-700 px-1.5 py-0.5 rounded ${s.status === "PUBLISHED" ? "bg-emerald-100 text-emerald-800" : s.status === "REJECTED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{s.status}</span>
              <span className="ml-auto flex gap-2">
                {s.status === "PENDING" && (
                  <>
                    <button onClick={() => { setEditing(s.id); setEf({ name: s.name, business_type: s.business_type, destination: s.destination, destination_slug: s.destination_slug, area: s.area, website: s.website, description: s.description, price_level: s.price_level }); }}
                      data-testid={`admin-sub-edit-${s.id}`} className="text-xs font-700 text-indigo-700 hover:underline inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> edit</button>
                    <button onClick={() => publish(s.id)} data-testid={`admin-sub-publish-${s.id}`} className="text-xs font-700 text-emerald-700 hover:underline inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> publish</button>
                    <button onClick={() => reject(s.id)} data-testid={`admin-sub-reject-${s.id}`} className="text-xs font-700 text-red-600 hover:underline inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> reject</button>
                  </>
                )}
              </span>
            </div>
            <p className="text-sm text-zinc-700 mt-1">{s.description}</p>
            <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
              {s.area && <span>Area: {s.area}</span>}{s.address && <span>Addr: {s.address}</span>}
              {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{s.website}</a>}
              {s.phone && <span>{s.phone}</span>}{s.contact_email && <span>{s.contact_email}</span>}
              {s.price_level && <span>Price: {s.price_level}</span>}
            </div>
            {editing === s.id && (
              <div className="mt-3 pt-3 border-t grid sm:grid-cols-2 gap-2" data-testid={`admin-sub-editform-${s.id}`}>
                {["name", "business_type", "destination", "destination_slug", "area", "website", "price_level"].map((k) => (
                  <input key={k} value={ef[k] || ""} onChange={(e) => setEf({ ...ef, [k]: e.target.value })} placeholder={k} className={inp} data-testid={`admin-sub-field-${k}`} />
                ))}
                <textarea value={ef.description || ""} onChange={(e) => setEf({ ...ef, description: e.target.value })} rows={2} className={`${inp} sm:col-span-2 resize-y`} />
                <div className="flex gap-2 sm:col-span-2">
                  <button onClick={saveEdit} data-testid={`admin-sub-save-${s.id}`} className="px-3 py-1.5 text-xs font-700 text-white bg-indigo-600">Save changes</button>
                  <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs border border-zinc-300">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
